/**
 * What the active route's environment id points at in the cloud project catalog.
 *
 * Route attachment asks the catalog three separate questions — which row is
 * this, did the owner stop it, and what connect metadata does it yield — and
 * every one of them has a wrong-looking obvious answer that a caller reaches
 * for otherwise: match on `runtimeId` alone and a handed-off runtime resolves
 * to nothing; read `status` for a stop and a warm sprite reports `suspended`;
 * build metadata inline and each entry point carries a different subset of the
 * row. Keeping the three together is what keeps those answers one answer.
 */
import {
  buildCloudProjectIdentityByRuntimeId,
  resolveCloudProjectEnvironmentId,
} from "../components/sidebar/cloudProjectsGroup.logic";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import type { SidebarPreConnectCloudMetadata } from "../components/sidebar/SidebarProjectItem";
import type { CloudRuntimeProjectHandoff } from "../components/sidebar/cloudRuntimeProjectHandoffStore";
import type { CloudProjectCatalogRow } from "../cmmd/cmmdForgeRuntimeListResource";

export interface ResolvedCloudRouteRow {
  readonly row: CloudProjectCatalogRow;
  readonly runtimeId: string | null;
  readonly matchedByHandoff: boolean;
}

/**
 * Resolve the catalog row an environment id refers to.
 *
 * Handles BOTH cloud environment shapes: a provisioned `cmmd-runtime:<id>`
 * environment, and the `cmmd-cloud-project:<org>:<project>` placeholder a
 * registered-but-unprovisioned project carries. Landing on the placeholder used
 * to fall through to "not a cloud route", which silently produced a blank page
 * with no explanation and no way to start the workspace.
 */
export function findCloudRowForEnvironment(
  environmentId: string | null | undefined,
  catalogRows: readonly CloudProjectCatalogRow[],
  handoffs: readonly CloudRuntimeProjectHandoff[],
  nowMs?: number,
): ResolvedCloudRouteRow | null {
  if (!environmentId) return null;
  // The durable Environment id survives Sprite replacement. Prefer it before
  // reading the legacy runtime locator, so a persisted R1 route follows the
  // catalog's R2 row without being misclassified as a desktop Environment.
  const durableRow = catalogRows.find(
    (candidate) => candidate.cmmdEnvironmentId === String(environmentId),
  );
  if (durableRow) {
    return { row: durableRow, runtimeId: durableRow.runtimeId, matchedByHandoff: false };
  }
  const runtimeId = classifyEnvironmentId(environmentId).legacyRuntimeId;
  if (runtimeId) {
    const exactRow = catalogRows.find((candidate) => candidate.runtimeId === runtimeId);
    if (exactRow) return { row: exactRow, runtimeId, matchedByHandoff: false };
    const identity = buildCloudProjectIdentityByRuntimeId(catalogRows, handoffs, nowMs).get(
      runtimeId,
    );
    if (!identity) return null;
    const handoffRow = catalogRows.find(
      (candidate) =>
        candidate.cmmdOrganizationId === identity.cmmdOrganizationId &&
        candidate.cmmdProjectId === identity.cmmdProjectId,
    );
    return handoffRow ? { row: handoffRow, runtimeId, matchedByHandoff: true } : null;
  }
  const placeholderRow = catalogRows.find(
    (candidate) =>
      candidate.runtimeId === null &&
      resolveCloudProjectEnvironmentId(candidate) === String(environmentId),
  );
  return placeholderRow ? { row: placeholderRow, runtimeId: null, matchedByHandoff: false } : null;
}

export function createCloudRuntimeMetadata(
  row: CloudProjectCatalogRow,
  runtimeId = row.runtimeId,
): SidebarPreConnectCloudMetadata {
  return {
    runtimeId,
    cmmdOrganizationId: row.cmmdOrganizationId,
    cmmdProjectId: row.cmmdProjectId,
    // FOR-32652. Carried so a deep-link or reload connect names the same
    // Environment the sidebar would have named. Dropping it here would make the
    // durable identifier depend on WHICH entry point the user came through.
    cmmdEnvironmentId: row.cmmdEnvironmentId,
    registeredAt: row.registeredAt,
    projectName: row.projectName,
    githubRepoFullName: row.githubRepoFullName,
    workspaceRoot: row.workspaceRoot,
    iconUrl: row.iconUrl,
    icon: row.icon,
    status: row.status,
    desiredState: row.desiredState,
  };
}

/**
 * Did the OWNER stop this runtime?
 *
 * Not the same question as "is it idle". `stopMachine` on Sprites is
 * `suspendSprite`, so a stopped runtime's sprite goes warm exactly like an idle
 * one and the reconciler overwrites `status: "stopped"` with `"suspended"`.
 * Only `desiredState` survives that, and it is the field CMMD's own connect
 * admission gate reads — so reading anything else here guarantees the two sides
 * disagree, which is precisely how a thread click turned into a 409 "Forge
 * runtime is stopped. Start it before connecting." instead of a Start button.
 */
export function isStoppedByOperator(row: CloudProjectCatalogRow): boolean {
  return row.desiredState === "stopped";
}
